'use client';

import React, { useEffect, useMemo } from 'react';
import { Avatar, Box, Breadcrumbs, Button, CircularProgress, Link, Typography } from '@mui/material';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MetaTags } from '../../components/Metatags/Metatags';
import { manageAudition } from '../../redux/audition/actions';
import { managedAuditionWithSubmissionsSelector } from '../../selectors/selectors';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ROUTES } from '../../types/global/routes.types';
import { IApplication } from '../../types/global/ListRender.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_LOW } from './brandDashboardTokens';

function applicantName(app: IApplication) {
    const u = app.user;
    if (!u) return 'Applicant';
    const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return full || u.userName || 'Applicant';
}

export default function BrandAuditionParticipantPage() {
    const { auditionId, participantId } = useParams<{ auditionId: string; participantId: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { applications, loading } = useSelector(managedAuditionWithSubmissionsSelector);

    useEffect(() => {
        if (auditionId) {
            dispatch(manageAudition({ auditionId, page: 1, limit: 100 }));
        }
    }, [auditionId, dispatch]);

    const application = useMemo(
        () => applications?.find(a => a._id === participantId || a.id === participantId),
        [applications, participantId],
    );

    const detailPath = ROUTES.BRAND_AUDITION_DETAIL.replace(':auditionId', auditionId || '');
    const workProfilePath = application?.user?.userName
        ? replaceRouteParam(ROUTES.WORK_PROFILE, 'userName', application.user.userName)
        : undefined;

    const title = application ? `${applicantName(application)} | Application` : 'Application';

    if (loading && !application) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: BRAND_LUXE_PRIMARY }} />
            </Box>
        );
    }

    if (!application) {
        return (
            <MetaTags title="Application" description="Participant detail">
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: 'rgba(255,255,255,0.6)' }}>
                        Application not found.
                    </Typography>
                    <Button sx={{ mt: 2, color: BRAND_LUXE_PRIMARY }} onClick={() => navigate(detailPath)}>
                        Back to audition
                    </Button>
                </Box>
            </MetaTags>
        );
    }

    const name = applicantName(application);
    const initials = name
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2);

    return (
        <MetaTags title={title} description="Participant detail">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    sx={{
                        alignSelf: 'flex-start',
                        '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.35)' },
                    }}>
                    <Link
                        component={RouterLink}
                        to={ROUTES.BRAND_CAMPAIGNS}
                        underline="hover"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.75)',
                            '&:hover': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        Campaigns
                    </Link>
                    <Link
                        component={RouterLink}
                        to={detailPath}
                        underline="hover"
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 13,
                            color: 'rgba(255,255,255,0.75)',
                            '&:hover': { color: BRAND_LUXE_PRIMARY },
                        }}>
                        Auditions
                    </Link>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 13, color: BRAND_LUXE_PRIMARY }}>
                        {name}
                    </Typography>
                </Breadcrumbs>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { sm: 'center' },
                        gap: 3,
                        p: 3,
                        borderRadius: 2,
                        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                        border: '1px solid rgba(255,255,255,0.06)',
                    }}>
                    <Avatar
                        src={application.user?.profile_picture || undefined}
                        sx={{
                            width: 72,
                            height: 72,
                            bgcolor: 'rgba(255,201,101,0.12)',
                            color: BRAND_LUXE_PRIMARY,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 22,
                        }}>
                        {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 20, md: 24 },
                                color: '#fff',
                                mb: 0.5,
                            }}>
                            {name}
                        </Typography>
                        {application.user?.userName ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.5)',
                                    mb: 1,
                                }}>
                                @{application.user.userName}
                            </Typography>
                        ) : null}
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: 'rgba(255,255,255,0.65)',
                                textTransform: 'capitalize',
                            }}>
                            Status: {String(application.filing_status).replace(/_/g, ' ')}
                        </Typography>
                        {application.role?.character ? (
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    fontSize: 13,
                                    color: BRAND_LUXE_PRIMARY,
                                    mt: 0.5,
                                }}>
                                Role: {application.role.character}
                            </Typography>
                        ) : null}
                    </Box>
                    {workProfilePath ? (
                        <Button
                            variant="outlined"
                            startIcon={<OpenInNewOutlinedIcon />}
                            onClick={() => navigate(workProfilePath)}
                            sx={{
                                alignSelf: { xs: 'stretch', sm: 'center' },
                                borderColor: 'rgba(255,201,101,0.35)',
                                color: BRAND_LUXE_PRIMARY,
                                textTransform: 'none',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                '&:hover': { borderColor: BRAND_LUXE_PRIMARY },
                            }}>
                            Full workspace
                        </Button>
                    ) : null}
                </Box>
            </Box>
        </MetaTags>
    );
}
